// The board itself, and everything drawn behind the chrome.

import { useEffect, useState } from "react";

import { useGameState } from "src/client/ai/hooks/selectors";
import { useGame } from "../useGame";
import { packFromUrl, type PiecePack } from "src/client/ai/render/models";
import { BoardCanvas } from "../BoardCanvas";
import type { CameraPrefs } from "../cameraPrefs";
import { DespairBanner } from "../DespairBanner";
import type { Despairing } from "src/engine/session/despairView";
import { DevMenu } from "../DevMenu";
import { loadPack } from "../packPref";
import { PlanningDrawer } from "../PlanningDrawer";
import { styled } from "../kit";
import type { Tool } from "../tool";
import type { BoardInteraction } from "../useBoardInteraction";
import { useBoardHandle } from "../shell/BoardHandle";
import { useShellMode } from "../shell/ShellMode";

/**
 * Behind everything, and the full window. Pointer events land here wherever no
 * panel is covering it, which is what keeps clicking the board the ordinary
 * thing to do.
 *
 * **No `zIndex` on purpose.** Giving this layer one makes it a stacking
 * context, and then the HUD inside it — objectives, the despair banner — is
 * trapped below the panel however high its own `zIndex` climbs, because it is
 * competing with its siblings rather than with the panel. Without one, the
 * canvas sits at the bottom by DOM order and the HUD can rise above the
 * interface.
 */
const Layer = styled("div")({ position: "absolute", inset: 0 });

export function BoardLayer({
  board_,
  despair,
  despairEscape,
  onCelebrate,
  camera,
  paceScale,
  maxFps,
  tool,
  onHover
}: {
  board_: BoardInteraction;
  /** The army about to lay down its arms, and what would stop it. */
  despair: Despairing | undefined;
  despairEscape: { pieces: number; material: number } | undefined;
  onCelebrate: (kind: "stage") => void;
  camera: CameraPrefs;
  /** How fast turns play back — one number, and every clock takes it. */
  paceScale: number;
  maxFps?: number;
  tool: Tool;
  onHover: (over: boolean) => void;
}) {
  const board = useBoardHandle();
  const state = useGameState();
  const game = useGame();
  const { scenario, watching, onLoadLevel } = useShellMode();

  // Developer view: what the opponent is planning, while it plans.
  const [planning, setPlanning] = useState(false);
  /**
   * The piece set every army wears unless it has been dressed individually.
   *
   * Three sources, most specific first: **the URL wins**, then the player's
   * stored choice, then the classic set. The order is the argument each one
   * makes — `?pack=` is a link somebody was handed to look at a build, so it
   * has to survive whatever that person happens to have chosen, exactly as
   * `?room=` overrides the menu. Read once: the menu is where it is picked and
   * the dev menu is where it is toggled mid-game.
   */
  const [pack, setPack] = useState<PiecePack>(() => packFromUrl() ?? loadPack());

  // Machines act on their own at the end of a turn, so pulse whatever fired.
  // Without this the only sign a rook had minted was a new piece appearing.
  const minted = state.lastTick?.minted;
  useEffect(() => {
    for (const entry of minted ?? []) {
      board.current?.pulse(entry.square);
      board.current?.pulse(entry.at);
    }
  }, [board, minted]);

  /**
   * Is a *machine* about to move?
   *
   * What the plan drawer is gated on, and it used to be
   * `position.turn !== localSide` — which is never true in the two modes where
   * there is most to watch. `localSide` falls back to the side to move when
   * there is no seat (hot-seat, and watching), so the drawer could not appear
   * in either: the button flipped its own label and nothing else happened.
   *
   * The same test the driver makes, deliberately: `useSoloOpponent` is enabled
   * on `scenario` and plays for every side that is not the seat, with `NOBODY`
   * as the seat while watching. If that call changes, this has to change with
   * it — there is no plan to show for a side no machine is planning for.
   */
  const machineToMove = Boolean(scenario) && (watching || state.position.turn !== 0);

  return (
    <Layer>
      {/* An army in a coma, while there is still time to do something about
          it. Silent until now: a side was diagnosed, given three turns and
          swept into scrap with nothing on screen ever changing. */}
      {despair && <DespairBanner despair={despair} escape={despairEscape} />}
      <DevMenu
        // The player's own door (§29 U4): a dev command is still a command,
        // and one issued against a step of history would land on a board
        // nobody is looking at. The panel prefers `game.test` and falls back
        // to this on a deployment where the role, not the build, opened it.
        dispatch={board_.send}
        onCelebrate={onCelebrate}
        onLoadLevel={onLoadLevel}
        planning={planning}
        canPlan={machineToMove}
        onPlanning={setPlanning}
        pack={pack}
        onPack={setPack}
      />
      {/* **During the other side's turn only.** On your own turn the brief
          shown would be *yours*, which is a different thing entirely and
          reads as the tool being wrong rather than as a different subject.
          The board is frozen while the opponent thinks, so recomputing the
          brief here gives the same answer the worker is working from. */}
      {planning && machineToMove && <PlanningDrawer state={state} />}
      <BoardCanvas
        ref={board}
        game={game}
        // Every gesture is the session's: the canvas drives `game.session`.
        onHoverChange={onHover}
        pack={pack}
        projection={camera.perspective ? "perspective" : "orthographic"}
        // Across the table or straight down — a remembered preference, pushed
        // like the projection because the renderer is built in an effect.
        tilt={camera.tilt}
        // The one borrowed look the renderer starts itself (an unmasking), so
        // the two toggles have to be pushed to it rather than read by `look`.
        followMoves={camera.autoFocus && camera.trackPiece}
        // The second of the two queues the toggle relaxes: the factory's
        // beat waits for the pieces unless it is on.
        staging={!camera.opponentFast}
        // The other half of the speed control the driver's clock takes above.
        // 1 with no transport up and the animation speed left alone, so an
        // ordinary game at ordinary speed is untouched.
        paceScale={paceScale}
        // Scenario games only, and off by default — see `render/frameCap.ts`.
        maxFps={maxFps}
        tool={tool}
      />
    </Layer>
  );
}
